'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface EcosystemObservation {
  id: string;
  user_id: string;
  photo_url?: string;
  reflection?: string;
  divine_names?: string[];
  biome_detected?: string;
  species_detected?: string[];
  auto_tags?: string[];
  gallery_visible?: boolean;
  approved?: boolean | null;
  created_at: string;
  location_label?: string;
  submission_note?: string;
}

type ReviewAction = 'approved' | 'rejected' | 'gallery_on' | 'gallery_off';

const GUARDIAN_EMAILS = ['admin@haalproject.com', 'guardian@haalproject.com'];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function GuardianReviewPanel() {
  const supabase = createClient();

  const [user, setUser] = useState<any>(null);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [observations, setObservations] = useState<EcosystemObservation[]>([]);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [noteMap, setNoteMap] = useState<Record<string, string>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (user && GUARDIAN_EMAILS.includes(user.email || '')) {
        setAuthorized(true);
        loadObservations();
      } else {
        setLoading(false);
      }
    });
  }, []);

  const loadObservations = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ecosystem_observations')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setObservations(data as EcosystemObservation[]);
    setLoading(false);
  }, []);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAction = async (obs: EcosystemObservation, action: ReviewAction) => {
    setActionLoading(obs.id + action);
    try {
      // Update ecosystem_observations
      const updates: Partial<EcosystemObservation> = {};
      if (action === 'approved') updates.approved = true;
      if (action === 'rejected') updates.approved = false;
      if (action === 'gallery_on') updates.gallery_visible = true;
      if (action === 'gallery_off') updates.gallery_visible = false;

      const { error: updateError } = await supabase
        .from('ecosystem_observations')
        .update(updates)
        .eq('id', obs.id);

      if (updateError) throw updateError;

      // Log the review action
      await supabase.from('guardian_review_log').insert({
        observation_id: obs.id,
        guardian_id: user?.id,
        action,
        note: noteMap[obs.id] || null,
      });

      // Update local state
      setObservations(prev =>
        prev.map(o => o.id === obs.id ? { ...o, ...updates } : o)
      );
      showToast(`Observation ${action.replace('_', ' ')} successfully`, 'success');
    } catch (err: any) {
      showToast(err.message || 'Action failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredObs = observations.filter(o => {
    if (filter === 'pending') return o.approved === null || o.approved === undefined;
    if (filter === 'approved') return o.approved === true;
    if (filter === 'rejected') return o.approved === false;
    return true;
  });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em' }}>
          Loading observations…
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#c47a7a', letterSpacing: '.1em' }}>
          ✗ Guardian access required
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          background: toast.type === 'success' ? 'rgba(45,122,71,0.95)' : 'rgba(196,122,122,0.95)',
          border: `1px solid ${toast.type === 'success' ? '#2d7a47' : '#c47a7a'}`,
          borderRadius: 6,
          padding: '10px 18px',
          fontFamily: 'monospace',
          fontSize: 10,
          color: '#F5F0E8',
          letterSpacing: '.06em',
        }}>
          {toast.type === 'success' ? '✓' : '✗'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', marginBottom: 8 }}>
          ◎ Community Ecosystem · Guardian Review
        </p>
        <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#F5F0E8', fontWeight: 400, marginBottom: 10 }}>
          Ecosystem Observations
        </h2>
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, maxWidth: 600 }}>
          Review community-submitted ecosystem observations — photos, reflections, and Divine Name resonances. Approve or reject submissions and toggle gallery visibility before publishing to the community.
        </p>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', count: observations.length, color: 'rgba(212,197,169,0.5)' },
          { label: 'Pending', count: observations.filter(o => o.approved === null || o.approved === undefined).length, color: '#c9a84c' },
          { label: 'Approved', count: observations.filter(o => o.approved === true).length, color: '#2d7a47' },
          { label: 'Rejected', count: observations.filter(o => o.approved === false).length, color: '#c47a7a' },
          { label: 'In Gallery', count: observations.filter(o => o.gallery_visible === true).length, color: '#2d8a87' },
        ].map(stat => (
          <div key={stat.label} style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 6,
            padding: '10px 16px',
            textAlign: 'center',
          }}>
            <div style={{ fontFamily: 'monospace', fontSize: 18, color: stat.color, fontWeight: 700 }}>{stat.count}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {(['pending', 'approved', 'rejected', 'all'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '10px 18px',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${filter === f ? '#c9a84c' : 'transparent'}`,
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: 9,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: filter === f ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              transition: 'all 0.15s',
            }}
          >
            {f}
          </button>
        ))}
        <button
          onClick={loadObservations}
          style={{
            marginLeft: 'auto',
            padding: '8px 14px',
            background: 'rgba(45,138,135,0.08)',
            border: '1px solid rgba(45,138,135,0.2)',
            borderRadius: 4,
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: 8,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: '#2d8a87',
          }}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Observation cards */}
      {filteredObs.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em' }}>
            No {filter === 'all' ? '' : filter} observations found
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredObs.map(obs => {
            const isPending = obs.approved === null || obs.approved === undefined;
            const isExpanded = expandedId === obs.id;
            const statusColor = obs.approved === true ? '#2d7a47' : obs.approved === false ? '#c47a7a' : '#c9a84c';
            const statusLabel = obs.approved === true ? 'Approved' : obs.approved === false ? 'Rejected' : 'Pending';

            return (
              <div
                key={obs.id}
                style={{
                  background: 'rgba(6,13,18,0.8)',
                  border: `1px solid ${isExpanded ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  borderLeft: `3px solid ${statusColor}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                {/* Card header */}
                <div
                  style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}
                  onClick={() => setExpandedId(isExpanded ? null : obs.id)}
                >
                  {/* Photo thumbnail */}
                  {obs.photo_url ? (
                    <img
                      src={obs.photo_url}
                      alt="Ecosystem observation"
                      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{
                      width: 56, height: 56, borderRadius: 4, flexShrink: 0,
                      background: 'rgba(45,138,135,0.1)',
                      border: '1px solid rgba(45,138,135,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, color: 'rgba(45,138,135,0.4)',
                    }}>◎</div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 3,
                        background: `${statusColor}15`, border: `1px solid ${statusColor}40`, color: statusColor,
                        letterSpacing: '.08em', textTransform: 'uppercase',
                      }}>{statusLabel}</span>
                      {obs.gallery_visible && (
                        <span style={{
                          fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 3,
                          background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.3)', color: '#2d8a87',
                          letterSpacing: '.08em',
                        }}>◎ In Gallery</span>
                      )}
                      {obs.biome_detected && (
                        <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em' }}>
                          {obs.biome_detected}
                        </span>
                      )}
                    </div>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.7)', margin: 0, lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {obs.reflection || 'No reflection provided'}
                    </p>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', marginTop: 4, letterSpacing: '.04em' }}>
                      {new Date(obs.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {obs.location_label && ` · ${obs.location_label}`}
                    </div>
                  </div>

                  {/* Divine names */}
                  {obs.divine_names && obs.divine_names.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 160 }}>
                      {obs.divine_names.slice(0, 3).map(name => (
                        <span key={name} style={{
                          fontFamily: "'Amiri', serif", fontSize: 10, padding: '2px 6px', borderRadius: 3,
                          background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.2)', color: '#c9a84c',
                        }}>{name}</span>
                      ))}
                    </div>
                  )}

                  <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 12 }}>{isExpanded ? '▲' : '▼'}</span>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '18px 18px 20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                      {/* Left: full reflection + photo */}
                      <div>
                        {obs.photo_url && (
                          <img
                            src={obs.photo_url}
                            alt="Full ecosystem observation"
                            style={{ width: '100%', borderRadius: 6, marginBottom: 12, maxHeight: 240, objectFit: 'cover' }}
                          />
                        )}
                        {obs.reflection && (
                          <div>
                            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
                              Reflection
                            </div>
                            <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.75)', lineHeight: 1.7, margin: 0 }}>
                              {obs.reflection}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Right: metadata */}
                      <div>
                        {obs.divine_names && obs.divine_names.length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
                              Divine Names Detected
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {obs.divine_names.map(name => (
                                <span key={name} style={{
                                  fontFamily: "'Amiri', serif", fontSize: 12, padding: '3px 10px', borderRadius: 4,
                                  background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.25)', color: '#c9a84c',
                                }}>{name}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {obs.species_detected && obs.species_detected.length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
                              Species Detected
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {obs.species_detected.map(sp => (
                                <span key={sp} style={{
                                  fontFamily: 'monospace', fontSize: 8, padding: '2px 8px', borderRadius: 3,
                                  background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', color: '#2d8a87',
                                }}>{sp}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {obs.auto_tags && obs.auto_tags.length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
                              Auto Tags
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {obs.auto_tags.map(tag => (
                                <span key={tag} style={{
                                  fontFamily: 'monospace', fontSize: 7, padding: '2px 6px', borderRadius: 3,
                                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.5)',
                                }}>{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Guardian note */}
                        <div>
                          <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
                            Guardian Note (optional)
                          </div>
                          <textarea
                            value={noteMap[obs.id] || ''}
                            onChange={e => setNoteMap(prev => ({ ...prev, [obs.id]: e.target.value }))}
                            placeholder="Add a note for this review…"
                            rows={3}
                            style={{
                              width: '100%',
                              background: 'rgba(255,255,255,0.03)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: 4,
                              padding: '8px 10px',
                              fontFamily: "'Georgia', serif",
                              fontSize: 12,
                              color: 'rgba(212,197,169,0.7)',
                              resize: 'vertical',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleAction(obs, 'approved')}
                        disabled={actionLoading !== null || obs.approved === true}
                        style={{
                          fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                          padding: '8px 16px', borderRadius: 4, cursor: obs.approved === true ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(45,122,71,0.4)', background: obs.approved === true ? 'rgba(45,122,71,0.15)' : 'rgba(45,122,71,0.08)',
                          color: obs.approved === true ? '#2d7a47' : 'rgba(45,122,71,0.7)',
                        }}
                      >
                        {actionLoading === obs.id + 'approved' ? '◌' : '✓'} Approve
                      </button>

                      <button
                        onClick={() => handleAction(obs, 'rejected')}
                        disabled={actionLoading !== null || obs.approved === false}
                        style={{
                          fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                          padding: '8px 16px', borderRadius: 4, cursor: obs.approved === false ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(196,122,122,0.4)', background: obs.approved === false ? 'rgba(196,122,122,0.15)' : 'rgba(196,122,122,0.06)',
                          color: obs.approved === false ? '#c47a7a' : 'rgba(196,122,122,0.7)',
                        }}
                      >
                        {actionLoading === obs.id + 'rejected' ? '◌' : '✗'} Reject
                      </button>

                      <div style={{ width: 1, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />

                      <button
                        onClick={() => handleAction(obs, 'gallery_on')}
                        disabled={actionLoading !== null || obs.gallery_visible === true}
                        style={{
                          fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                          padding: '8px 16px', borderRadius: 4, cursor: obs.gallery_visible ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(45,138,135,0.4)', background: obs.gallery_visible ? 'rgba(45,138,135,0.15)' : 'rgba(45,138,135,0.06)',
                          color: obs.gallery_visible ? '#2d8a87' : 'rgba(45,138,135,0.7)',
                        }}
                      >
                        {actionLoading === obs.id + 'gallery_on' ? '◌' : '◎'} Show in Gallery
                      </button>

                      <button
                        onClick={() => handleAction(obs, 'gallery_off')}
                        disabled={actionLoading !== null || obs.gallery_visible === false || obs.gallery_visible === undefined}
                        style={{
                          fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                          padding: '8px 16px', borderRadius: 4, cursor: (!obs.gallery_visible) ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
                          color: 'rgba(212,197,169,0.4)',
                        }}
                      >
                        {actionLoading === obs.id + 'gallery_off' ? '◌' : '◉'} Hide from Gallery
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
